import S3FileUpload from 'src/helpers/ReactS3Lib/ReactS3';
import { S3BucketConfig } from 'src/common/constants';
import moment from 'moment';
import {
  attachmentKey,
  imageName,
} from 'src/components/AnnotateImage/helpers';
import {
  ServiceRequestCreationPayload,
  SeparatedAttachments,
  ServiceRequestAttachment,
  ServiceRequestFormData,
  ServiceRequestTagsPayload,
} from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/serviceRequestFormtypes';

export type UploadResult =
  | { success: true; url: string; fileName: string }
  | { success: false; error: string; fileName: string };

export type DeleteFileS3Result =
  | { success: true }
  | { success: false; error: string };

/**
 * Converts a base64 data URL into a real, uploadable File.
 *
 * markerjs renders the annotated image to a `data:` URL — i.e. a base64 *string*.
 * Sending that string as an attachment is what the endpoint rejects, so we turn
 * it back into binary here, in three steps:
 *
 *   1. `fetch(dataUrl)` resolves the data URL locally (no network) and decodes
 *      its base64 payload back into raw bytes.
 *   2. `Response.blob()` reads those bytes into a Blob, carrying the MIME type
 *      declared in the data URL (e.g. "image/png").
 *   3. We wrap the Blob in a named `File`, making it indistinguishable from a
 *      user-dropped upload so it rides the normal S3 path — no base64 string
 *      ever reaches the payload.
 *
 * Refs (file conversion):
 *   - `data:` URL scheme: https://developer.mozilla.org/en-US/docs/Web/URI/Reference/Schemes/data
 *   - Response.blob():    https://developer.mozilla.org/en-US/docs/Web/API/Response/blob
 */
export const dataUrlToFile = async (
  dataUrl: string,
  filename: string
): Promise<File> => {
  const dataUrlResponse: Response = await fetch(dataUrl);

  const annotatedImageBinary: Blob = await dataUrlResponse.blob();

  const inferredMimeType: string = annotatedImageBinary.type;

  const uploadableAnnotatedFile: File = new File(
    [annotatedImageBinary],
    filename,
    { type: inferredMimeType }
  );

  return uploadableAnnotatedFile;
};

const FILE_EXTENSION_PATTERN: RegExp = /\.[^.]+$/;
const ANNOTATED_FILE_SUFFIX: string = '-annotated.png';

/* Swaps every attachment the user annotated for an uploadable File built from
 * its annotation, keyed the same way the annotation UI keys them. Untouched
 * attachments pass through unchanged. The annotated File is shaped like a
 * freshly-dropped upload (`path` + `preview`) so it flows through the existing
 * separateAttachments -> uploadServiceRequestFileToS3 pipeline with no changes */
export const applyAnnotationsToAttachments = async (
  srAttachments: ServiceRequestAttachment[],
  annotations: Record<string, string>
): Promise<ServiceRequestAttachment[]> => {
  const attachmentsReadyForUpload: ServiceRequestAttachment[] =
    await Promise.all(
      srAttachments.map(async (stagedAttachment: any) => {
        const annotationKey: string = attachmentKey(stagedAttachment);

        const annotatedDataUrl: string | undefined =
          annotations[annotationKey];

        if (!annotatedDataUrl) {
          return stagedAttachment;
        }

        const originalFileName: string = imageName(stagedAttachment);

        const nameWithoutExtension: string = originalFileName.replace(
          FILE_EXTENSION_PATTERN,
          ''
        );

        const annotatedFileName: string =
          nameWithoutExtension + ANNOTATED_FILE_SUFFIX;

        const annotatedImageFile: any = await dataUrlToFile(
          annotatedDataUrl,
          annotatedFileName
        );

        annotatedImageFile.path = annotatedFileName;
        annotatedImageFile.preview = annotatedDataUrl;

        return annotatedImageFile;
      })
    );

  return attachmentsReadyForUpload;
};

const separateAttachments = (
  srAttachments: ServiceRequestAttachment[]
): SeparatedAttachments => {
  const filesToUpload: ServiceRequestAttachment[] = [];
  const existingUrls: string[] = [];

  srAttachments.forEach((attachment: any) => {
    if (attachment.preview) {
      filesToUpload.push(attachment);
    } else {
      existingUrls.push(attachment.url);
    }
  });

  return { filesToUpload, existingUrls };
};

export const prepareServiceRequestCreationPayload = (formData: ServiceRequestFormData, uploadedUrls: string[]): ServiceRequestCreationPayload => {

  const formDataTags = formData.tags || [];
  
  const tags: ServiceRequestTagsPayload[] = formDataTags.map(tag => {
    if (tag.__isNew__) {
      return { name: tag.value };
    }
    else {
      return { id: tag.value };
    }
  });

  return {
    description: formData.description,
    attachments: uploadedUrls,
    schedulePreferences: formData.schedulePreferences,
    propertyId: formData.propertyId,
    suiteId: formData.suiteId,
    assetId: formData.assetId,
    itemId: formData.itemId,
    issueTypeId: formData.issueTypeId,
    tags: tags
  };
}

const uploadServiceRequestFileToS3 = async (
  file: any
): Promise<UploadResult> => {
  try {
    if (!file || !file.path) {
      return {
        success: false,
        error: 'Invalid file or file path missing',
        fileName: file?.name || 'unknown',
      };
    }

    // Generate timestamped filename.
    const filenameParts = file.path.split(/\.(?=[^\.]+$)/);

    const newFileName: string =
      filenameParts[0] +
      moment().format('MMDDYYYYhmmss') +
      '.' +
      filenameParts[1];

    const data = await S3FileUpload.uploadFile(
      file,
      S3BucketConfig,
      newFileName
    );

    return {
      success: true,
      url: data.location,
      fileName: newFileName,
    };
  } catch (error: any) {
    console.error('Error uploading file to S3:', error);

    return {
      success: false,
      error: error?.message || 'Upload failed',
      fileName: file?.path || 'unknown',
    };
  }
};

const deleteServiceRequestFileFromS3 = async (
  fileUrl: string
): Promise<DeleteFileS3Result> => {
  try {
    if (!fileUrl) {
      return {
        success: false,
        error: 'File URL is required',
      };
    }

    await S3FileUpload.deleteFile(fileUrl);

    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Delete failed',
    };
  }
};

export {
  uploadServiceRequestFileToS3,
  deleteServiceRequestFileFromS3,
  separateAttachments,
};
